import React from "react";
import Icon from "components/AppIcon";
import { useAuth } from "../../../contexts/AuthContext";
import { useSubscriptionLimits } from "../../../hooks/useSubscriptionLimits";
import { useNavigate } from "react-router-dom";

const RESOURCES = [
  { key: "branches",  label: "Branches",  icon: "Building2"  },
  { key: "members",   label: "Members",   icon: "Users"      },
  { key: "staff",     label: "Staff",     icon: "UserCheck"  },
  { key: "trainers",  label: "Trainers",  icon: "Dumbbell"   },
];

function UsageBar({ current, max }) {
  if (max === -1) return (
    <div className="flex items-center gap-1.5">
      <div className="flex-1 h-1.5 rounded-full bg-[var(--color-success)]/20">
        <div className="h-full w-full rounded-full bg-[var(--color-success)]/40" />
      </div>
      <span className="text-[10px] font-[var(--font-data)] text-[var(--color-success)] whitespace-nowrap">Unlimited</span>
    </div>
  );

  const pct = max > 0 ? Math.min((current / max) * 100, 100) : 0;
  const atLimit = current >= max;
  const nearLimit = pct >= 80;

  const barColor = atLimit
    ? "var(--color-error)"
    : nearLimit
    ? "var(--color-warning)"
    : "var(--color-success)";

  return (
    <div className="flex items-center gap-1.5">
      <div className="flex-1 h-1.5 rounded-full bg-[var(--color-muted)]">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: barColor }}
        />
      </div>
      <span
        className="text-[10px] font-[var(--font-data)] whitespace-nowrap"
        style={{ color: barColor }}
      >
        {current}/{max}
      </span>
    </div>
  );
}

export default function PlanUsageSummary() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { limits, usage, planName, loading } = useSubscriptionLimits(user?.id);

  const atAnyLimit = limits && RESOURCES.some(({ key }) => {
    const max = limits?.[`max_${key}`];
    return max !== -1 && usage?.[key] >= max;
  });

  const nearAnyLimit = limits && RESOURCES.some(({ key }) => {
    const max = limits?.[`max_${key}`];
    if (max === -1) return false;
    return usage?.[key] / max >= 0.8;
  });

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-[var(--shadow-sm)] p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-[var(--color-primary)]/10 flex items-center justify-center">
            <Icon name="BarChart3" size={14} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">
              Plan Usage
            </h3>
            {!loading && planName && (
              <p className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                {planName} plan
              </p>
            )}
          </div>
        </div>

        {(atAnyLimit || nearAnyLimit) && !loading && (
          <button
            onClick={() => navigate("/vendor-plan-selection")}
            className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-semibold font-[var(--font-caption)] bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors"
          >
            <Icon name="ArrowUpCircle" size={11} color="white" />
            Upgrade
          </button>
        )}
      </div>

      {/* Resource rows */}
      {loading ? (
        <div className="space-y-3">
          {RESOURCES.map(({ key }) => (
            <div key={key} className="space-y-1.5">
              <div className="h-3 w-20 bg-[var(--color-muted)] animate-pulse rounded" />
              <div className="h-1.5 w-full bg-[var(--color-muted)] animate-pulse rounded-full" />
            </div>
          ))}
        </div>
      ) : !limits ? (
        <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
          No active subscription found.
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
          {RESOURCES.map(({ key, label, icon }) => {
            const max = limits?.[`max_${key}`];
            const current = usage?.[key] ?? 0;
            const atLimit = max !== -1 && current >= max;
            const nearLimit = max !== -1 && current / max >= 0.8;

            return (
              <div key={key}>
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon
                    name={icon}
                    size={11}
                    color={
                      atLimit
                        ? "var(--color-error)"
                        : nearLimit
                        ? "var(--color-warning)"
                        : "var(--color-muted-foreground)"
                    }
                  />
                  <span className="text-xs font-medium font-[var(--font-caption)] text-[var(--color-foreground)]">
                    {label}
                  </span>
                  {atLimit && (
                    <span className="ml-auto text-[9px] font-bold font-[var(--font-caption)] px-1 py-0.5 rounded bg-[var(--color-error)]/10 text-[var(--color-error)]">
                      LIMIT
                    </span>
                  )}
                  {!atLimit && nearLimit && (
                    <span className="ml-auto text-[9px] font-bold font-[var(--font-caption)] px-1 py-0.5 rounded bg-[var(--color-warning)]/10 text-[var(--color-warning)]">
                      NEAR
                    </span>
                  )}
                </div>
                <UsageBar current={current} max={max} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
